import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { primaryTint } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import AgentAvatar from 'components/@home/@settings/Tabs/Agents/AgentAvatar';
import { getAgentCardGradient } from 'components/@home/@settings/Tabs/Agents/agentColorPalette';
import OverflowTip from 'components/common/OverflowTip';
import SearchField from 'components/controls/SearchField';
import AddIcon from 'mdi-react/AddIcon';
import ChatOutlineIcon from 'mdi-react/ChatOutlineIcon';
import DeleteIcon from 'mdi-react/DeleteIcon';
import DotsVerticalIcon from 'mdi-react/DotsVerticalIcon';
import EditIcon from 'mdi-react/EditIcon';
import ExportIcon from 'mdi-react/ExportIcon';
import ImportIcon from 'mdi-react/ImportIcon';
import PhoneOutlineIcon from 'mdi-react/PhoneOutlineIcon';
import PlusCircleIcon from 'mdi-react/PlusCircleIcon';
import RobotOutlineIcon from 'mdi-react/RobotOutlineIcon';
import SitemapOutlineIcon from 'mdi-react/SitemapOutlineIcon';
import ViewGridOutlineIcon from 'mdi-react/ViewGridOutlineIcon';
import ViewListOutlineIcon from 'mdi-react/ViewListOutlineIcon';
import { arrayOf, bool, func, object } from 'prop-types';
import React, { useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    padding: theme.spacing(3, 4, 2, 4),
    '& .MuiButton-root': {
      textWrap: 'nowrap',
      minWidth: 'auto',
      lineHeight: 1,
    },
  },
  titleBox: {
    minWidth: 180,
  },
  title: {
    fontWeight: 600,
  },
  subtitle: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  searchWrap: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    minWidth: 200,
    maxWidth: 420,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  plus: {
    fontSize: 18,
    marginRight: '5px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gridAutoRows: 'min-content',
    alignContent: 'start',
    gap: theme.spacing(3),
    padding: theme.spacing(1, 4, 4),
    overflowY: 'auto',
    flex: 1,
  },
  card: {
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '16px',
    backgroundColor: theme.palette.background.paper,
    transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
    overflow: 'hidden',
    '&:hover': {
      borderColor: theme.palette.text.disabled,
      boxShadow: '0 12px 28px -14px rgba(15, 23, 42, 0.22)',
    },
  },
  actionArea: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  cardBody: {
    padding: theme.spacing(1.5, 2, 2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flex: 1,
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    minHeight: 28,
  },
  modeChip: {
    width: 24,
    height: 24,
    borderRadius: '6px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
  },
  menuBtn: {
    opacity: 0,
    transition: 'opacity 160ms ease',
    'div:hover > &, &:focus, &:focus-visible': {
      opacity: 1,
    },
  },
  menuBtnVisible: {
    opacity: 1,
  },
  avatarArea: {
    padding: theme.spacing(1, 0, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  name: {
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 700,
    textAlign: 'center',
    lineHeight: 1.3,
    maxWidth: '100%',
    color: theme.palette.text.primary,
  },
  desc: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    textAlign: 'center',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    padding: theme.spacing(0, 1),
    marginTop: theme.spacing(0.25),
  },
  cardFooter: {
    marginTop: 'auto',
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    minHeight: 48,
    backgroundColor: theme.palette.background.paper,
  },
  stat: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(12.5),
    color: theme.palette.text.secondary,
  },
  noActivity: {
    fontSize: theme.typography.pxToRem(12.5),
    color: theme.palette.text.disabled,
  },
  addCard: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    backgroundColor: theme.palette.background.paper,
    transition: 'all 160ms ease',
    gap: theme.spacing(1),
    alignSelf: 'stretch',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: primaryTint(theme, 0.04),
    },
  },
  listWrap: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(0, 4, 4),
  },
});

const formatCount = n => {
  if (!n) return null;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
};

const AgentsList = ({
  flows,
  hasAgentic = false,
  onSelect,
  onCreate,
  onImport,
  onEdit,
  onExport,
  onDelete,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [view, setView] = useState('grid');
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuFlow, setMenuFlow] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return flows;
    return flows.filter(
      f =>
        (f.name || '').toLowerCase().includes(q) || (f.description || '').toLowerCase().includes(q),
    );
  }, [flows, search]);

  const openMenu = (e, flow) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuFlow(flow);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuFlow(null);
  };

  const renderActivity = flow => {
    const chatCount = flow.stats?.chatCount;
    const voiceCount = flow.stats?.voiceCount;
    if (!chatCount && !voiceCount) {
      return <Typography sx={styles.noActivity}>{I18n.t('AgenticChatFlow.NoActivity')}</Typography>;
    }
    return (
      <>
        {chatCount ? (
          <Box component="span" sx={styles.stat}>
            <ChatOutlineIcon size={14} />
            {formatCount(chatCount)}
          </Box>
        ) : null}
        {voiceCount ? (
          <Box component="span" sx={styles.stat}>
            <PhoneOutlineIcon size={14} />
            {formatCount(voiceCount)}
          </Box>
        ) : null}
      </>
    );
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.toolbar}>
        <Box sx={styles.titleBox}>
          <Typography variant="h5" sx={styles.title}>
            {I18n.t(hasAgentic ? 'AgenticChatFlow.AgentsTitle' : 'ChatFlowSettings.Flows')}
          </Typography>
          <Typography sx={styles.subtitle}>
            {I18n.t(hasAgentic ? 'AgenticChatFlow.AgentsSubtitle' : 'ChatFlowSettings.Flows')}
          </Typography>
        </Box>
        <Box sx={styles.searchWrap}>
          <SearchField
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={I18n.t('AgenticChatFlow.SearchAgents')}
          />
        </Box>
        <Box sx={styles.actions}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
          >
            <ToggleButton value="grid" aria-label={I18n.t('AgenticChatFlow.ViewGrid')}>
              <ViewGridOutlineIcon size={18} />
            </ToggleButton>
            <ToggleButton value="list" aria-label={I18n.t('AgenticChatFlow.ViewList')}>
              <ViewListOutlineIcon size={18} />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button variant="outlined" color="secondary" onClick={onImport}>
            <SvgIcon sx={styles.plus}>
              <ImportIcon />
            </SvgIcon>
            {I18n.t('AgenticChatFlow.ImportAgent')}
          </Button>
          <Button variant="contained" color="primary" onClick={onCreate}>
            <SvgIcon sx={styles.plus}>
              <PlusCircleIcon />
            </SvgIcon>
            {I18n.t(hasAgentic ? 'AgenticChatFlow.AddNewAgent' : 'ChatFlowSettings.Create flow')}
          </Button>
        </Box>
      </Box>

      {view === 'grid' ? (
        <Box sx={styles.grid}>
          {filtered.map(flow => {
            const isHover = hoveredId === flow._id;
            return (
              <Card
                key={flow._id}
                sx={styles.card}
                elevation={0}
                onMouseEnter={() => setHoveredId(flow._id)}
                onMouseLeave={() => setHoveredId(prev => (prev === flow._id ? null : prev))}
              >
                <CardActionArea
                  sx={styles.actionArea}
                  onClick={() => onSelect(flow._id)}
                  component="div"
                >
                  <Box
                    sx={[
                      styles.cardBody,
                      {
                        background: getAgentCardGradient(flow, theme.palette.mode),
                      },
                    ]}
                  >
                    <Box sx={styles.cardTop}>
                      <Tooltip
                        title={I18n.t(
                          flow.mode === 'agent'
                            ? 'ChatFlowSettings.AgentFlow'
                            : 'ChatFlowSettings.FlowTypePages',
                        )}
                      >
                        <Box component="span" sx={styles.modeChip}>
                          {flow.mode === 'agent' ? (
                            <RobotOutlineIcon size={14} />
                          ) : (
                            <SitemapOutlineIcon size={14} />
                          )}
                        </Box>
                      </Tooltip>
                      <IconButton
                        size="small"
                        sx={[styles.menuBtn, isHover && styles.menuBtnVisible]}
                        onClick={e => openMenu(e, flow)}
                        aria-label={I18n.t('ChatFlowSettings.Edit')}
                      >
                        <DotsVerticalIcon size={18} />
                      </IconButton>
                    </Box>
                    <Box sx={styles.avatarArea}>
                      <AgentAvatar flow={flow} size={92} animated={isHover} />
                    </Box>
                    <Typography sx={styles.name} noWrap>
                      <OverflowTip value={flow.name || ''} />
                    </Typography>
                    <Typography sx={styles.desc}>
                      {flow.description || I18n.t('AgenticChatFlow.NoDescription')}
                    </Typography>
                  </Box>
                  <Box sx={styles.cardFooter}>{renderActivity(flow)}</Box>
                </CardActionArea>
              </Card>
            );
          })}
          {hasAgentic && (
            <Box
              sx={styles.addCard}
              onClick={onCreate}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') onCreate();
              }}
            >
              <AddIcon size={28} />
              <Typography variant="body2">{I18n.t('AgenticChatFlow.AddNewAgent')}</Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={styles.listWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={56} />
                <TableCell>{I18n.t('AgenticChatFlow.Name')}</TableCell>
                <TableCell>{I18n.t('AgenticChatFlow.Description')}</TableCell>
                <TableCell width={120} />
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(flow => (
                <TableRow
                  key={flow._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onSelect(flow._id)}
                >
                  <TableCell>
                    <AgentAvatar flow={flow} size={36} />
                  </TableCell>
                  <TableCell>
                    <OverflowTip value={flow.name}>{flow.name}</OverflowTip>
                  </TableCell>
                  <TableCell>
                    <OverflowTip value={flow.description || ''}>
                      {flow.description || '—'}
                    </OverflowTip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant={flow.mode === 'agent' ? 'filled' : 'outlined'}
                      label={I18n.t(
                        flow.mode === 'agent'
                          ? 'ChatFlowSettings.AgentFlow'
                          : 'ChatFlowSettings.Flows',
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={e => openMenu(e, flow)}>
                      <DotsVerticalIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            onEdit(menuFlow);
            closeMenu();
          }}
        >
          <EditIcon size={16} style={{ marginRight: 8 }} />
          {I18n.t('ChatFlowSettings.Edit')}
        </MenuItem>
        {menuFlow?.mode !== 'agent' && (
          <MenuItem
            onClick={() => {
              onExport(menuFlow);
              closeMenu();
            }}
          >
            <ExportIcon size={16} style={{ marginRight: 8 }} />
            {I18n.t('ChatFlowSettings.Export')}
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            onDelete(menuFlow);
            closeMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon size={16} style={{ marginRight: 8 }} />
          {I18n.t('ChatFlowSettings.Delete')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

AgentsList.propTypes = {
  flows: arrayOf(object).isRequired,
  hasAgentic: bool,
  onSelect: func.isRequired,
  onCreate: func.isRequired,
  onImport: func.isRequired,
  onEdit: func.isRequired,
  onExport: func.isRequired,
  onDelete: func.isRequired,
};

export default AgentsList;
